import { useEffect, useRef, useState } from 'react'
import useCurrentReport from '../../../../hooks/useCurrentReport'
import useUpdateReportDoc from '../../../../hooks/useUpdateReportDoc'
import { IncidentSubmission } from '../../../../types/MonthlyReport'
import { journalTemplate, minCharacters } from '../utils/constants'
import { ClinicalObservationsState } from '../utils/types'

const defaultIncidents: IncidentSubmission = {
  gateway: null,
  injury: { reported: null, items: [] },
  surgery: { reported: null, items: [] },
  illness: { reported: null, items: [] },
  stress: { reported: null, items: [] },
  hospital: {
    reported: null,
    erVisit: null,
    hospitalised: null,
    daysHospitalised: null,
  },
}

export const useClinicalObservationsState = (): ClinicalObservationsState => {
  const [clinicalObservations, setClinicalObservations] =
    useState(journalTemplate)
  const [incidents, setIncidents] =
    useState<IncidentSubmission>(defaultIncidents)
  const report = useCurrentReport()
  const updateReport = useUpdateReportDoc()

  // Skip the first run of the auto-save effect (initial mount)
  const skipNextIncidentsEffect = useRef(true)
  // Prevent auto-save when incidents are populated from Firestore
  const hydratingIncidents = useRef(false)

  useEffect(() => {
    if (typeof report?.clinicalObservations === 'string') {
      setClinicalObservations(report.clinicalObservations)
    }
    if (report?.incidents) {
      hydratingIncidents.current = true
      setIncidents(report.incidents)
    }
  }, [report])

  // Auto-save incidents to Firestore whenever the user changes them
  useEffect(() => {
    if (skipNextIncidentsEffect.current) {
      skipNextIncidentsEffect.current = false
      return
    }
    if (hydratingIncidents.current) {
      hydratingIncidents.current = false
      return
    }
    updateReport({ incidents })
    // updateReport identity is stable; incidents is the only meaningful dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents])

  const isValid =
    clinicalObservations.length > journalTemplate.length + minCharacters

  return {
    clinicalObservations,
    setClinicalObservations,
    isValid,
    incidents,
    setIncidents,
  }
}
